import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Icon from 'dpl/common/components/Icon';
import Label from 'dpl/form/components/Label';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import FixedTooltipPortal from 'dpl/common/tooltip/components/FixedTooltipPortal';
import GenericMenu from 'dpl/path_to_payment/components/GenericMenu';
import BottomSheetMenu from 'dpl/common/design_system/BottomSheetMenu';

import FancyDropdown, {
  FancyDropdownMenu,
  FancyDropdownMenuItem,
  FancyDropdownToggle
} from '../FancyDropdown';
import DropdownButton from './DropdownButton';

const IS_LT_MD = isLessThanBreakpoint('md');

export default function ResponsiveSelect({
  options = [],
  selectedValue,
  onChange,
  label,
  placeholder = 'Select',
  placeholderClassName,
  id,
  className,
  disabled = false,
  error = null,
  loading = false,
  newDesignSystemStyles
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  function handleChange(val) {
    onChange(val);
    setIsDropdownOpen(false);
  }

  const selectedOption = useMemo(
    () => options.find(option => option.value === selectedValue),
    [options, selectedValue]
  );
  const displayValue = selectedOption ? selectedOption.label : placeholder;

  return (
    <div className={classnames('ResponsiveSelect', className)}>
      {label && <Label htmlFor={id} newDesignSystemStyles text={label} />}
      <FancyDropdown
        id={id}
        isOpen={isDropdownOpen}
        onChange={setIsDropdownOpen}
      >
        <FancyDropdownToggle
          disabled={disabled || loading}
          className={classnames('bg-white ba br3 b--default pv3 ph3 font-16', {
            'b--red': error,
            disabled: disabled || loading
          })}
          id={`FancyDropdownToggle_${id}`}
        >
          <div className="flex items-center">
            <span
              className={classnames('truncate font-16 w-100', {
                disabled: selectedValue === undefined,
                [placeholderClassName]: placeholderClassName && !selectedOption
              })}
            >
              {displayValue}
            </span>
            {!disabled && !loading && (
              <Icon
                name={
                  isDropdownOpen ? 'fetch-chevron-up' : 'fetch-chevron-down'
                }
              />
            )}
            {loading && <Icon name="spinner" className="animate-spin" />}
          </div>
        </FancyDropdownToggle>
        {IS_LT_MD && newDesignSystemStyles && (
          <BottomSheetMenu
            isOpen={isDropdownOpen}
            onClose={() => setIsDropdownOpen(false)}
            options={options}
            onSelect={handleChange}
            disabled={disabled || loading}
          />
        )}
        {IS_LT_MD && !newDesignSystemStyles && (
          <FixedTooltipPortal
            isVisible={isDropdownOpen}
            onCloseClick={() => setIsDropdownOpen(false)}
            style={{ maxHeight: '90vh' }}
            shouldHideCloseButton
            className="no-scrollbars"
          >
            <GenericMenu
              disabled={disabled || loading}
              setIsMenuOpen={setIsDropdownOpen}
              className="ph1"
            >
              {options.map((option, index) => (
                <React.Fragment key={option.value}>
                  {index > 0 &&
                    option.section !== options[index - 1]?.section && (
                      <span className="b--light-gray mv2 db bt" />
                    )}
                  <DropdownButton
                    className={classnames(
                      newDesignSystemStyles ? 'pv4 ph2' : 'pv2'
                    )}
                    option={option}
                    onChange={handleChange}
                  />
                </React.Fragment>
              ))}
            </GenericMenu>
          </FixedTooltipPortal>
        )}
        {!IS_LT_MD && (
          <FancyDropdownMenu
            className={classnames('ResponsiveSelect__menu flex-auto mt1', {
              'br3 box-shadow-2 bg-white overflow-hidden':
                !newDesignSystemStyles
            })}
            newDesignSystemStyles={newDesignSystemStyles}
          >
            {options.map((option, index) => (
              <React.Fragment key={option.value}>
                {index > 0 &&
                  option.section !== options[index - 1]?.section && (
                    <span className="b--light-gray mv2 db bt" />
                  )}
                <FancyDropdownMenuItem
                  itemKey={option.value || ''}
                  className="bg-white pointer"
                >
                  <DropdownButton
                    className={classnames(
                      'ph2',
                      newDesignSystemStyles ? 'pv4' : 'pv2'
                    )}
                    option={option}
                    onChange={handleChange}
                  />
                </FancyDropdownMenuItem>
              </React.Fragment>
            ))}
          </FancyDropdownMenu>
        )}
      </FancyDropdown>
      {error && <div className="red font-14 mt1">{error}</div>}
    </div>
  );
}

ResponsiveSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  placeholderClassName: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  loading: PropTypes.bool,
  newDesignSystemStyles: PropTypes.bool
};

ResponsiveSelect.defaultProps = {
  selectedValue: undefined,
  label: null,
  placeholder: 'Select',
  placeholderClassName: null,
  id: 'ResponsiveSelect',
  className: null,
  disabled: false,
  error: null,
  loading: false,
  newDesignSystemStyles: false
};
